/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   filterTextHelpers
 */

import { ListingAmenity, PropertyFeatures } from '@zcode/enums'
import type { Filters } from '@zcode/state-type'
import { PRICE_SORT_ASC, PRICE_SORT_DESC } from '../../constants/filterConstants'
import {
  APARTMENT_TYPE_GROUP,
  CONDO_TYPE_GROUP,
  HOUSE_TYPE_GROUP,
  LONG_TERM_OTHER_TYPE_GROUP,
  ROOM_TYPE_GROUP,
  TOWNHOUSE_TYPE_GROUP,
} from '../../constants/propertyTypesConstants'

export const getPetsFilterText = (filters: Partial<Filters>) => {
  const { cats, dogs } = filters
  if (cats && dogs) {
    return 'pet friendly'
  }
  if (cats) {
    return 'cat friendly'
  }
  if (dogs) {
    return 'dog friendly'
  }
  return ''
}

export const getLowIncomeText = (filters: Partial<Filters>) => {
  const { features } = filters || []
  if (features?.includes(PropertyFeatures.INCOME_RESTRICTED)) {
    return 'low income'
  }
  return ''
}

export const getMoveInSpecialText = (filters: Partial<Filters>) => {
  const { orFeatures } = filters || []
  if (orFeatures?.[0]?.includes(PropertyFeatures.SPECIALS)) {
    return 'move in special'
  }
  return ''
}

export const getUtilitiesIncludedFilterText = (filters: Partial<Filters>) => {
  const { orFeatures } = filters || []
  if (orFeatures?.[0]?.includes(PropertyFeatures.UTILITIES_INCLUDED)) {
    return 'utilities included'
  }
  return ''
}

export const getNoSecurityDepositFilterText = (filters: Partial<Filters>) => {
  const { orFeatures } = filters || []
  if (orFeatures?.[0]?.includes(PropertyFeatures.NO_SECURITY_DEPOSIT)) {
    return 'no security deposit'
  }
  return ''
}

export const getFurnishedFilterText = (filters: Partial<Filters>) => {
  if (filters?.listingAmenities?.includes(ListingAmenity.FURNISHED)) {
    return 'furnished'
  }
  return ''
}

export const getShortTermFilterText = (filters: Partial<Filters>) => {
  if (filters?.shortTerm && !filters?.longTerm) {
    return 'short term'
  }

  return ''
}

export const groupPropertyTypesByTopCategories = (propertyTypes: Filters['propertyTypes']) => {
  return {
    apartment: APARTMENT_TYPE_GROUP.every((type: number) => propertyTypes.includes(type)),
    house: HOUSE_TYPE_GROUP.every((type: number) => propertyTypes.includes(type)),
    condo: CONDO_TYPE_GROUP.every((type: number) => propertyTypes.includes(type)),
    room: ROOM_TYPE_GROUP.every((type: number) => propertyTypes.includes(type)),
    townhouse: TOWNHOUSE_TYPE_GROUP.every((type: number) => propertyTypes.includes(type)),
    other: LONG_TERM_OTHER_TYPE_GROUP.every((type: number) => propertyTypes.includes(type)),
  }
}

export const isMultiplePropertyCategories = (filters: Partial<Filters>) => {
  if (!filters?.propertyTypes) {
    return false
  }
  const { propertyTypes } = filters
  const { house, room, apartment, townhouse, other, condo } =
    groupPropertyTypesByTopCategories(propertyTypes)
  if (house && townhouse && !apartment && !condo && !room && !other) {
    return false
  }
  if (other || [apartment, house, room, condo, townhouse].filter((x) => x).length >= 2) {
    return true
  }
  return false
}

export enum PropertyCategory {
  APARTMENTS = 'apartments',
  CONDOS = 'condos',
  HOUSES = 'houses',
  ROOMS = 'rooms',
  TOWNHOUSES = 'townhouses',
}

export const getPropertyCategoriesFilterText = (filters: Partial<Filters>) => {
  if (!filters?.propertyTypes) {
    return PropertyCategory.APARTMENTS
  }
  const { propertyTypes } = filters
  const { house, room, apartment, townhouse, other, condo } =
    groupPropertyTypesByTopCategories(propertyTypes)
  if (house && townhouse && !apartment && !condo && !room && !other) {
    return PropertyCategory.HOUSES
  }
  if (other || [apartment, house, room, condo, townhouse].filter((x) => x).length >= 2) {
    return PropertyCategory.APARTMENTS
  }
  if (apartment) {
    return PropertyCategory.APARTMENTS
  }
  if (house) {
    return PropertyCategory.HOUSES
  }
  if (room) {
    return PropertyCategory.ROOMS
  }
  if (condo) {
    return PropertyCategory.CONDOS
  }
  if (townhouse) {
    return PropertyCategory.TOWNHOUSES
  }
  return PropertyCategory.APARTMENTS
}

export const getCheapFilterText = (filters: Pick<Filters, 'sort'>) => {
  return filters.sort[0] === PRICE_SORT_ASC[0] ? 'cheap' : ''
}

export const getLuxuryFilterText = (filters: Pick<Filters, 'sort'>) => {
  return filters.sort[0] === PRICE_SORT_DESC[0] ? 'luxury' : ''
}
